// Utilities
import { defineStore } from 'pinia';
import { useShellStore } from '@maru/shell-app';
import i18n from '@/plugins/i18n';
import { useStorage } from '@vueuse/core';
import { Settings } from '@/constants/Settings';
import { isEmbedded } from '@/constants/config';
import { ProjectUserRole } from '@/constants/roles';
import { previewCommentsState } from '@/composables/velt/commentsSettingsState';

export const useAppStore = defineStore('app', {
  state: () => ({
    loading: false,
    locale: useStorage('locale', i18n.global.locale.value as string),
    sessionData: null,
    previewQueryLocale: null,
    // Stable survey/version identifier captured before redirect to /:sessionId.
    previewSurveyId: null as string | null,
    previewQuestionsVersionId: null as string | null,
    previewSurveyVersionId: null as string | null,
    projectSettings: null as Settings | null,// This will be populated by the main bridge only if this app is loaded as an iframe within QuestDIY.
    // The current user's project role (lead/participant/observer), resolved from
    // core's GetProjectById via the main bridge. Null until loaded (or non-embedded).
    userRole: null as ProjectUserRole | null,
    /** Synced from survey-builder via sb-bridge SbEvent.Init (and local panel resize). */
    veltPanelWidth: 380,
    /** Synced from survey-builder via sb-bridge SbEvent.Init for first-load open state. */
    veltPanelOpen: false,
  }),
  getters: {
    isSurveyPreview(state): boolean {
      // previewSurveyId is restored from history on reload before sessionData
      // is fetched again via /do.
      return !!state.sessionData?.preview || !!state.previewSurveyId
    },
    /** True when running inside the QuestDIY shell iframe (builder context). */
    isEmbedded(): boolean {
      return isEmbedded()
    },
    /**
     * The `project_settings_preview_comments_everyone` flag. When embedded it
     * comes from the authoritative draft project settings loaded over the main
     * bridge; for direct-link (non-embedded) sessions those settings are never
     * populated, so fall back to the survey session response variables.
     */
    previewCommentsEveryone(state): boolean {
      if (!this.isEmbedded) {
        return state.sessionData?.variables?.project_settings_preview_comments_everyone === 'true'
      }
      // Embedded: read the live panel state so flipping a switch updates the
      // comment gates immediately, without waiting for a persist round-trip.
      return previewCommentsState.everyone
    },
    /**
     * The `project_settings_preview_comments_restricted` flag: when set, only
     * the emails in {@link previewCommentsEmails} may view preview comments.
     */
    previewCommentsRestricted(): boolean {
      return previewCommentsState.restricted
    },
    /** Normalised, lower-cased allow-list of emails permitted to comment. */
    previewCommentsEmails(): string[] {
      return previewCommentsState.emails
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean)
    },
    /** True when at least one preview-comment access option is enabled. */
    previewCommentsAnyOptionEnabled(): boolean {
      return this.previewCommentsEveryone || this.previewCommentsRestricted
    },
    /** True when the current (embedded) user's email is in the allow-list. */
    currentUserInPreviewCommentsAllowList(): boolean {
      const email = useShellStore().user?.email?.trim().toLowerCase()
      if (!email) return false
      return this.previewCommentsEmails.includes(email)
    },
    /** The current user's resolved project role (defaults to not-a-member). */
    projectUserRole(state): ProjectUserRole {
      return state.userRole ?? ProjectUserRole.NotMember
    },
    isLead(): boolean {
      return this.projectUserRole === ProjectUserRole.Lead
    },
    isParticipant(): boolean {
      return this.projectUserRole === ProjectUserRole.Participant
    },
    isObserver(): boolean {
      return this.projectUserRole === ProjectUserRole.Observer
    },
    /**
     * Whether the Velt preview-comment surfaces (panel thread + inline pins)
     * may be shown. Nobody — not even a lead — sees comments unless at least
     * one access option is enabled. This gate is intentionally separate from
     * {@link canShowCommentsSettings}: leads/participants can still open the
     * "Settings & Permissions" tab to turn an option on even while no comments
     * are visible. Beyond the option gate, by role:
     *  - Lead:        yes.
     *  - Participant: yes, unless the project is restricted to an email
     *                 allow-list their address is not on (and "everyone" is off).
     *  - Observer:    yes (any option being on is sufficient).
     *  - Direct link (non-embedded): only when the project opts everyone in.
     */
    canShowPreviewComments(): boolean {

      if (!this.isSurveyPreview) return false
      // No option enabled → no comments for anyone (leads included).
      if (!this.previewCommentsAnyOptionEnabled) return false
      if (!this.isEmbedded) {
        return this.previewCommentsEveryone
      }
      if (this.isLead) return true
      if (this.isParticipant) {
        if (this.previewCommentsEveryone) return true
        if (this.previewCommentsRestricted) {
          return this.currentUserInPreviewCommentsAllowList
        }
        return true
      }
      if (this.isObserver) return true
      return false
    },
    /**
     * The "Settings & Permissions" tab is only meaningful in the embedded
     * builder context. Leads and participants may manage settings; observers
     * (and non-members) never can.
     */
    canShowCommentsSettings(): boolean {
      if (!this.isSurveyPreview || !this.isEmbedded) return false
      return this.isLead || this.isParticipant
    },
    /**
     * Whether the "Comments" tab (the actual comment thread, not settings) is
     * usable. Equivalent to being permitted to view comments — which already
     * requires at least one access option to be enabled.
     */
    canUseCommentsTab(): boolean {
      return this.canShowPreviewComments
    },
    /**
     * Whether the Velt SDK should be initialised and the panel toggle shown at
     * all. This is broader than {@link canShowPreviewComments}: a participant
     * who is restricted out of *viewing* comments (email not on the allow-list)
     * must still be able to open the panel to reach the "Settings & Permissions"
     * tab. Actual comment surfaces remain gated on `canShowPreviewComments`.
     */
    shouldInitVelt(): boolean {
      return this.canShowPreviewComments || this.canShowCommentsSettings
    },
    availableLanguages(state): string[] {
      return state.sessionData?.locale?.available || []
    },
    activeLocale(): string {
      const langs = this.availableLanguages
      const active = this.sessionData?.locale?.active

      if (active && langs.includes(active)) {
        return active
      }

      return this.locale || ''
    },
    themeLogo(state): string | null {
      return state.sessionData?.theme?.logo || null
    }
  }
})
